import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
// zod4Resolver, never zodResolver: the latter reads `parsed.error.errors`,
// which zod 4 does not have, so validation fails silently and an invalid value
// is submitted.
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useAuth } from '../auth/useAuth.js';
import { useCompany, useUpdateCompany } from '../api/hooks.js';
import type { Company } from '../api/hooks.js';

/**
 * Indstillinger — the company's own registration details.
 *
 * Four things the page says out loud, because each of them is easier to leave
 * implied and wrong:
 *
 * - **Only an owner may save.** A form that answers 403 is worse than no form.
 * - **The fiscal year has no year.** It is `MM-DD`, and 29 February is refused,
 *   because a financial year cannot begin on a day that does not exist in three
 *   years out of four.
 * - **Nothing reads the fiscal year yet.** The reports take an explicit period.
 *   Setting it records the company's year correctly and changes no report.
 * - **The VAT period does not create periods.** It records which cycle the
 *   company files on; periods are opened with their own dates on /moms.
 *
 * Currency and country are shown and not editable: every posted amount is
 * already in the company currency, so changing it would reinterpret the ledger
 * rather than convert it.
 */

const VAT_PERIOD_LABELS: Record<Company['vatPeriod'], MessageKey> = {
  monthly: 'settings.vatPeriod.monthly',
  quarterly: 'settings.vatPeriod.quarterly',
  biannual: 'settings.vatPeriod.biannual',
};

/**
 * `MM-DD`, and a real date.
 *
 * The month/day check is here as well as in the service, so a typo is caught
 * before a request rather than after one. The service remains the authority --
 * this is a courtesy, not the enforcement.
 */
const MONTH_DAY = /^(\d{2})-(\d{2})$/u;

export function isRealMonthDay(value: string): boolean {
  const match = MONTH_DAY.exec(value);
  if (match === null) {
    return false;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  // 2001 is not a leap year, so 02-29 fails here — deliberately. A financial
  // year cannot start on a date that three years in four do not have.
  const asDate = new Date(Date.UTC(2001, month - 1, day));
  return month >= 1 && asDate.getUTCMonth() === month - 1 && asDate.getUTCDate() === day;
}

function settingsSchema(t: (key: MessageKey) => string) {
  return z.object({
  name: z.string().trim().min(1, t('settings.nameRequired')).max(255),
  cvr: z
    .string()
    .trim()
    .refine((value) => value === '' || /^[0-9]{8}$/u.test(value), {
      message: t('settings.cvrInvalid'),
    }),
  address: z.string().trim().max(255),
  zip: z.string().trim().max(16),
  city: z.string().trim().max(128),
  fiscalYearStart: z
    .string()
    .trim()
    .refine(isRealMonthDay, {
      message: t('settings.fiscalStartInvalid'),
    }),
  vatPeriod: z.enum(['monthly', 'quarterly', 'biannual']),
  });
}

type SettingsValues = z.infer<ReturnType<typeof settingsSchema>>;

function valuesFrom(company: Company): SettingsValues {
  return {
    name: company.name,
    cvr: company.cvr ?? '',
    address: company.address ?? '',
    zip: company.zip ?? '',
    city: company.city ?? '',
    fiscalYearStart: company.fiscalYearStart,
    vatPeriod: company.vatPeriod,
  };
}

export function SettingsPage(): ReactNode {
  const t = useT();
  const { role, refresh } = useAuth();
  const company = useCompany();
  const save = useUpdateCompany();
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const mayEdit = role === 'owner' || role === 'system_admin';

  const form = useForm<SettingsValues>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      cvr: '',
      address: '',
      zip: '',
      city: '',
      fiscalYearStart: '01-01',
      vatPeriod: 'quarterly',
    },
    validate: zod4Resolver(settingsSchema(t)),
  });

  // Fill from the loaded company once, without an effect. Re-fills when the
  // company switcher points somewhere else.
  if (company.data !== undefined && loadedFor !== company.data.id) {
    setLoadedFor(company.data.id);
    form.setValues(valuesFrom(company.data));
    form.resetDirty(valuesFrom(company.data));
  }

  function handleSubmit(values: SettingsValues): void {
    const nameChanged = values.name.trim() !== (company.data?.name ?? '');

    save.mutate(
      {
        name: values.name.trim(),
        // An empty field means "not recorded", which is `null` — clearing it —
        // and not `''`, which the API's CVR pattern would refuse anyway.
        cvr: values.cvr.trim() === '' ? null : values.cvr.trim(),
        address: values.address.trim() === '' ? null : values.address.trim(),
        zip: values.zip.trim() === '' ? null : values.zip.trim(),
        city: values.city.trim() === '' ? null : values.city.trim(),
        fiscalYearStart: values.fiscalYearStart.trim(),
        vatPeriod: values.vatPeriod,
      },
      {
        onSuccess: (updated) => {
          notifications.show({
            color: 'green',
            title: t('settings.saved'),
            message: updated.name,
          });
          form.resetDirty(valuesFrom(updated));
          // The company switcher in the header reads the membership list from
          // the auth context, not this query, so a rename would otherwise leave
          // the old name on screen until the next sign-in.
          if (nameChanged) {
            void refresh();
          }
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>{t('settings.title')}</Title>

      {company.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {company.isError && (
        <ErrorMessage error={company.error} title={t('settings.loadFailed')} />
      )}

      {company.data !== undefined && (
        <>
          {!mayEdit && (
            /*
              Shown instead of a disabled form full of the reason nothing
              happens. PATCH is owner-only and would answer 403.
            */
            <Alert color="gray" variant="light" title={t('settings.ownerOnlyTitle')}>
              {t('settings.ownerOnly')}
            </Alert>
          )}

          {/*
            noValidate: the browser's own constraint validation blocks the
            submit event before Mantine sees it, so the Zod schema never runs and
            the user gets a browser tooltip in the browser's language instead of
            the message written in the schema.
          */}
          <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              <Card withBorder padding="md">
                <Stack gap="md">
                  <Text fw={500}>{t('settings.companySection')}</Text>

                  <TextInput
                    label={t('settings.name')}
                    withAsterisk
                    disabled={!mayEdit}
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                  />

                  <TextInput
                    label={t('settings.cvr')}
                    placeholder={t('settings.cvrPlaceholder')}
                    description={t('settings.cvrHelp')}
                    disabled={!mayEdit}
                    key={form.key('cvr')}
                    {...form.getInputProps('cvr')}
                  />

                  <TextInput
                    label={t('settings.address')}
                    disabled={!mayEdit}
                    key={form.key('address')}
                    {...form.getInputProps('address')}
                  />

                  <Group grow align="flex-start">
                    <TextInput
                      label={t('settings.zip')}
                      disabled={!mayEdit}
                      key={form.key('zip')}
                      {...form.getInputProps('zip')}
                    />
                    <TextInput
                      label={t('settings.city')}
                      disabled={!mayEdit}
                      key={form.key('city')}
                      {...form.getInputProps('city')}
                    />
                  </Group>
                </Stack>
              </Card>

              <Card withBorder padding="md">
                <Stack gap="md">
                  <Text fw={500}>{t('settings.fiscalSection')}</Text>

                  <TextInput
                    label={t('settings.fiscalStart')}
                    placeholder="01-01"
                    description={t('settings.fiscalStartHelp')}
                    disabled={!mayEdit}
                    key={form.key('fiscalYearStart')}
                    {...form.getInputProps('fiscalYearStart')}
                  />

                  <Alert color="blue" variant="light" title={t('settings.fiscalUnusedTitle')}>
                    {t('settings.fiscalUnused')}
                  </Alert>

                  <Select
                    label={t('settings.vatPeriod')}
                    data={Object.entries(VAT_PERIOD_LABELS).map(([value, key]) => ({
                      value,
                      label: t(key),
                    }))}
                    allowDeselect={false}
                    disabled={!mayEdit}
                    key={form.key('vatPeriod')}
                    {...form.getInputProps('vatPeriod')}
                  />

                  <Text size="sm" c="dimmed">
                    {t('settings.vatPeriodHelp')}
                  </Text>
                </Stack>
              </Card>

              <Card withBorder padding="md">
                <Stack gap="md">
                  <Text fw={500}>{t('settings.immutableSection')}</Text>

                  <Group grow align="flex-start">
                    <TextInput
                      label={t('settings.currency')}
                      value={company.data.currency}
                      disabled
                      readOnly
                    />
                    <TextInput
                      label={t('settings.country')}
                      value={company.data.country}
                      disabled
                      readOnly
                    />
                  </Group>

                  <Text size="sm" c="dimmed">
                    {t('settings.currencyHelp')}
                  </Text>
                </Stack>
              </Card>

              {save.isError && (
                <ErrorMessage error={save.error} title={t('settings.saveFailed')} />
              )}

              {mayEdit && (
                <Group>
                  <Button type="submit" loading={save.isPending}>
                    {t('common.save')}
                  </Button>
                </Group>
              )}
            </Stack>
          </form>
        </>
      )}
    </Stack>
  );
}
